import React, { useEffect, useState } from "react";
import { TrendingUp, Loader2, Package, Repeat, DollarSign, AlertTriangle } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { sku } from "@/lib/api";
import { formatIDR, formatNumber } from "@/lib/format";

const SectionCard = ({ title, icon: Icon, color, products, valueKey, valueLabel, valueFmt }) => (
  <Card className="border-gray-200">
    <CardContent className="p-5">
      <div className="flex items-center gap-2">
        <div className={`w-8 h-8 rounded-lg bg-${color}-50 flex items-center justify-center text-${color}-600`}><Icon size={16} /></div>
        <h3 className="font-heading font-semibold text-gray-900">{title}</h3>
      </div>
      <div className="mt-4 space-y-2">
        {products.length === 0 ? (
          <div className="text-center py-6 text-sm text-gray-500">Belum ada data</div>
        ) : products.slice(0, 5).map((p, i) => (
          <div key={p.id} className="flex items-center gap-3 py-2 border-b border-gray-100 last:border-0">
            <div className={`w-7 h-7 rounded-lg bg-${color}-50 text-${color}-700 flex items-center justify-center text-xs font-bold`}>{i + 1}</div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm text-gray-900 truncate">{p.product_name}</div>
              <div className="text-xs text-gray-500 font-mono truncate">{p.sku}</div>
            </div>
            <div className="text-right">
              <div className="text-sm font-semibold text-gray-900">{valueFmt(p[valueKey])}</div>
              <div className="text-[10px] text-gray-500">{valueLabel}</div>
            </div>
          </div>
        ))}
      </div>
    </CardContent>
  </Card>
);

export default function SKUAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { sku.analytics().then(setData).catch(() => {}).finally(() => setLoading(false)); }, []);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-emerald-600" /></div>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-gray-900">SKU Analytics</h1>
        <p className="text-sm text-gray-500 mt-1">Insight per produk: mana yang laris, mana yang untung, mana yang harus dihentikan.</p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <SectionCard title="Produk Terlaris" icon={Package} color="emerald" products={data?.best_sellers || []} valueKey="units_sold" valueLabel="unit" valueFmt={(v) => formatNumber(v)} />
        <SectionCard title="Repeat Order Tertinggi" icon={Repeat} color="blue" products={data?.highest_repeat || []} valueKey="repeat_rate" valueLabel="repeat rate" valueFmt={(v) => `${v}%`} />
        <SectionCard title="Margin Tertinggi" icon={DollarSign} color="amber" products={data?.highest_margin || []} valueKey="unit_margin_pct" valueLabel="margin" valueFmt={(v) => `${v}%`} />
        <SectionCard title="Retur Tertinggi" icon={AlertTriangle} color="red" products={data?.highest_returns || []} valueKey="total_returns" valueLabel="retur" valueFmt={(v) => formatNumber(v)} />
      </div>

      <Card className="border-gray-200">
        <CardContent className="p-6">
          <h3 className="font-heading font-semibold text-gray-900">Rekomendasi Aksi</h3>
          <div className="mt-4 grid md:grid-cols-2 gap-3">
            <div className="p-4 rounded-lg bg-emerald-50 border border-emerald-200">
              <div className="text-xs font-bold uppercase tracking-wider text-emerald-700">Bundling Ideas</div>
              <p className="mt-1 text-sm text-gray-700">Bundling produk margin tinggi dengan produk laris untuk naikkan AOV.</p>
            </div>
            <div className="p-4 rounded-lg bg-amber-50 border border-amber-200">
              <div className="text-xs font-bold uppercase tracking-wider text-amber-700">Naik Harga</div>
              <p className="mt-1 text-sm text-gray-700">Produk laris + margin rendah = kandidat kuat untuk naikkan harga 5-10%.</p>
            </div>
            <div className="p-4 rounded-lg bg-red-50 border border-red-200">
              <div className="text-xs font-bold uppercase tracking-wider text-red-700">Hentikan Promo</div>
              <p className="mt-1 text-sm text-gray-700">Produk margin rendah + banyak retur = hentikan iklan & fokus ke SKU lain.</p>
            </div>
            <div className="p-4 rounded-lg bg-blue-50 border border-blue-200">
              <div className="text-xs font-bold uppercase tracking-wider text-blue-700">Push Repeat</div>
              <p className="mt-1 text-sm text-gray-700">Produk dengan repeat rate tinggi = fokuskan campaign WhatsApp reorder di sini.</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
